import React from "react";
import PopoverMenu from "components/PopoverMenu";
import { buildingStore } from "store/Building/BuildingStore";
import clsx from "clsx";
import CircularProgressBar from "components/CircularProgressBar";
import LoadingIcon from "components/LoadingIcon";
import { FileOperationOUT } from "api-client";
import Tooltip from "components/Tooltip";
import Badge from "components/Badge";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

const FilesPopover = () => {
  const { t } = useTranslation();

  const activeFiles = buildingStore.filesCalculationStatus
    ?.filter((item) => item.ifc_file_reporting_status === "ACTIVE")
    .sort((a, b) =>
      (a.ifc_file_name as string).localeCompare(b.ifc_file_name as string)
    );
  const inactiveFiles = buildingStore.filesCalculationStatus
    ?.filter((item) => item.ifc_file_reporting_status === "HIDDEN")
    .sort((a, b) =>
      (a.ifc_file_name as string).localeCompare(b.ifc_file_name as string)
    );

  function fileRow(file: FileOperationOUT) {
    const percentage = (file.completed_chunks * 100) / file.total_chunks;
    return (
      <div
        key={file.ifc_file_id}
        className={clsx(
          "flex items-center justify-between text-gray-800 py-2 px-4",
          "border-gray-200 border-b last-of-type:border-b-0"
        )}
      >
        <Tooltip content={file.ifc_file_name} truncate className="mr-2">
          {file.ifc_file_name}
        </Tooltip>
        {file.status === "IN_PROGRESS" ? (
          file.completed_chunks === 0 ? (
            pendingView()
          ) : (
            isCalculatingView()
          )
        ) : (
          <Badge
            size="small"
            type="success"
            className="hover:!bg-green-100 !h-[18px]"
          >
            {t("dashboard.calculated")}
          </Badge>
        )}
      </div>
    );

    function isCalculatingView() {
      return file.total_chunks > 1 ? (
        <div className="flex items-center text-xs">
          <CircularProgressBar
            size={10}
            strokeWidth={3}
            textStyle="hidden"
            activeStroke="#4f46e5"
            percent={percentage}
            classsName="!w-[20px] !h-[20px] flex items-center"
          />
          <span className="ml-2">{`${Math.round(percentage)}%`}</span>
        </div>
      ) : (
        <LoadingIcon className="w-[17px] h-[17px]" strokeWidth={12} />
      );
    }

    function pendingView() {
      return (
        <Badge
          size="small"
          type="attention"
          className="hover:!bg-yellow-100 !h-[18px]"
        >
          {t("dashboard.pending")}
        </Badge>
      );
    }
  }

  if (
    !buildingStore.filesCalculationStatus ||
    buildingStore.filesCalculationStatus?.length <= 1
  )
    return null;

  return (
    <PopoverMenu
      panelClassName="ml-4"
      panelDivClassName="w-[330px] max-h-[330px] overflow-y-auto"
      content={
        <div className="text-sm">
          {activeFiles?.length ? (
            <>
              <div className="bg-gray-50 px-4 py-1 text-gray-500 border-b border-gray-300">
                {t("dashboard.activeFiles")}
              </div>
              <div>{activeFiles?.map((file) => fileRow(file))}</div>
            </>
          ) : null}
          {inactiveFiles?.length ? (
            <>
              <div className="bg-gray-50 px-4 py-1 text-gray-500 border-b border-gray-300 border-t">
                {t("dashboard.inActiveFiles")}
              </div>
              <div>{inactiveFiles?.map((file) => fileRow(file))}</div>
            </>
          ) : null}
        </div>
      }
    />
  );
};

export default observer(FilesPopover);
